import { useState } from "react";
import {
  ArrowRight,
  BarChart3,
  PartyPopper,
  Shield,
  Sparkles,
  Ticket,
} from "lucide-react";
import { Link } from "react-router-dom";
import { CountryPickerModal } from "../components/CountryPickerModal.tsx";

export const Landing = () => {
  const [isCountryPickerOpen, setIsCountryPickerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#141419] via-[#0c0c10] to-black text-white selection:bg-purple-600 selection:text-white relative overflow-hidden">
      {/* Ambient background glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-purple-600/10 blur-[140px] pointer-events-none rounded-full"
        aria-hidden="true"
      />

      {/* Hero Section */}
      <div className="container mx-auto px-4 pt-20 pb-28 relative z-10">
        <div className="text-center max-w-3xl mx-auto animate-fade-up">
          <img
            src="/logo.jpg"
            alt="Agent FOMO Logo"
            className="mx-auto h-36 sm:h-40 mb-8 rounded-2xl shadow-2xl border border-white/10 hover:scale-105 hover:border-purple-500/30 transition-all duration-300"
          />
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm font-medium mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>Your Underground Scene Guide</span>
          </div>
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold tracking-tight mb-6 text-white">
            Meet Agent FOMO
          </h1>
          <p className="text-lg sm:text-2xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed font-light">
            Discover exclusive events, secret parties, and underground
            gatherings in your city. Never miss out again.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              type="button"
              onClick={() => setIsCountryPickerOpen(true)}
              style={{ height: 48, minHeight: 48, boxSizing: "border-box" }}
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 h-12 min-h-[48px] rounded-full bg-purple-600 hover:bg-purple-500 text-white font-semibold transition-all hover:scale-105 cursor-pointer shadow-lg shadow-purple-900/40"
            >
              <span>Start Chatting</span>
              <ArrowRight className="ml-2 w-4 h-4" />
            </button>
            <Link
              to="/app"
              style={{ height: 48, minHeight: 48, boxSizing: "border-box" }}
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 h-12 min-h-[48px] rounded-full border border-purple-500/40 hover:border-purple-400 bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 font-semibold transition-all hover:scale-105 cursor-pointer"
            >
              <span>Go to App</span>
              <Sparkles className="ml-2 w-4 h-4 text-purple-400" />
            </Link>
            <Link
              to="/stats"
              style={{ height: 48, minHeight: 48, boxSizing: "border-box" }}
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 h-12 min-h-[48px] rounded-full border border-white/15 hover:border-white/30 bg-white/[0.04] hover:bg-white/10 text-gray-300 hover:text-white font-semibold transition-all hover:scale-105 cursor-pointer"
            >
              <span>Live Stats</span>
              <BarChart3 className="ml-2 w-4 h-4 text-purple-400" />
            </Link>
          </div>
        </div>
      </div>

      <CountryPickerModal
        isOpen={isCountryPickerOpen}
        onClose={() => setIsCountryPickerOpen(false)}
      />

      {/* Features */}
      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div
            className="glass-card p-8 animate-fade-up hover:border-purple-500/30 transition-all duration-300"
            style={{ animationDelay: "0.1s" }}
          >
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-6">
              <PartyPopper className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-white">
              Exclusive Events
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              From underground raves to intimate gatherings, find events that
              match your vibe.
            </p>
          </div>
          <div
            className="glass-card p-8 animate-fade-up hover:border-purple-500/30 transition-all duration-300"
            style={{ animationDelay: "0.2s" }}
          >
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-6">
              <Ticket className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-white">
              Best Deals
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Exclusive discounts and early bird offers for the hottest events.
            </p>
          </div>
          <div
            className="glass-card p-8 animate-fade-up hover:border-purple-500/30 transition-all duration-300"
            style={{ animationDelay: "0.3s" }}
          >
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-6">
              <Shield className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-white">
              Verified Events
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              All events are verified and curated for your peace of mind.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-12 border-t border-white/5 relative z-10">
        <div className="flex flex-col items-center mb-6">
          <img
            src="/logo.jpg"
            alt="Agent FOMO"
            className="h-12 w-auto opacity-80 rounded-lg text-center hover:opacity-100 transition-opacity"
          />
        </div>
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-6">
          <Link to="/stats" className="hover:text-white transition-colors">
            Live Stats
          </Link>
          <Link to="/app" className="hover:text-white transition-colors">
            Browse Events
          </Link>
          <Link to="/promote" className="hover:text-white transition-colors">
            Promote an Event
          </Link>
          <Link to="/docs" className="hover:text-white transition-colors">
            API Docs
          </Link>
          <Link to="/privacy" className="hover:text-white transition-colors">
            Privacy Policy
          </Link>
          <Link to="/terms" className="hover:text-white transition-colors">
            Terms & Conditions
          </Link>
          <Link
            to="/accessibility"
            className="hover:text-white transition-colors"
          >
            Accessibility
          </Link>
        </div>
        <div className="text-center text-xs text-gray-500">
          © {new Date().getFullYear()} Agent FOMO. All rights reserved.
        </div>
      </footer>
    </div>
  );
};
